/**
 * Determines if AI Studio button should be shown based on the abWeaverAiStudio experiment
 * Variant 1 (50%) shows the button, variant 0 (50%) is control
 * @param experimentValue - The experiment variant value (0-1)
 * @returns true if AI Studio button should be shown, false if it should be hidden
 */
export function shouldShowAiStudio(experimentValue: number | undefined): boolean {
  return experimentValue === 1;
}

/**
 * Determines if the experimental Weave Design Lab flow should be shown
 * Variant 1 (candidate) shows the new weave design flow, variant 0 (control) shows the upload flow
 * @param experimentValue - The experiment variant value (0-1)
 * @returns true if Weave Design Lab should be shown, false if Upload Artwork should be used
 */
export function shouldShowWeaveDesignLab(experimentValue: number | undefined): boolean {
  return experimentValue === 1;
}
/**
 * Determines if diverse surfaces (model selection) should be shown based on the abDiverseSurfaces experiment
 * Variant 1 (candidate) shows the model selection feature, variant 0 (control) hides it
 * @param experimentValue - The experiment variant value (0-1)
 * @returns true if diverse surfaces should be shown, false if it should be hidden
 */
export function shouldShowDiverseSurfaces(experimentValue: number | undefined): boolean {
  return experimentValue === 1;
}
/**
 * Determines if Take a Photo CTA should be shown based on the ab_take_a_photo experiment
 * Variant 1 (50%) shows the Take a Photo button, variant 0 (50%) is control (hides button)
 * @param experimentValue - The experiment variant value (0-1)
 * @returns true if Take a Photo button should be shown, false if it should be hidden
 */
export function shouldShowTakePhotoBtn(experimentValue: number | undefined): boolean {
  return experimentValue === 1;
}

/**
 * Determines if mobile navigation should be shown based on the ab_design_lab_mobile_navigation experiment
 * Variant 1 (50%) shows the mobile navigation, variant 0 (50%) is control
 * @param experimentValue - The experiment variant value (0-1)
 * @returns true if mobile tab navigation should be shown, false if next/back button should be shown
 */
export function getMobileNavigationVariant(experimentValue: number | undefined): number {
  return experimentValue ?? 0;
}

/**
 * Determines if desktop final verification modal should be shown
 * Desktop behavior is the same for both variant 1 and 2
 * Related to ab_design_lab_final_verification experiment
 * @param experimentValue - The experiment variant value (0-2)
 * @returns true if desktop verification should be shown (variants 1 or 2)
 */
export function shouldShowDesktopFinalVerification(experimentValue: number | undefined): boolean {
  return experimentValue === 1 || experimentValue === 2;
}

/**
 * Determines if this is mobile final verification variant 1 (popup on Front step)
 * Related to ab_design_lab_final_verification experiment
 * @param experimentValue - The experiment variant value (0-2)
 * @returns true if mobile variant 1
 */
export function isFinalVerificationMobileVariant1(experimentValue: number | undefined): boolean {
  return experimentValue === 1;
}

/**
 * Determines if this is mobile final verification variant 2 (summary with green screen default)
 * Related to ab_design_lab_final_verification experiment
 * @param experimentValue - The experiment variant value (0-2)
 * @returns true if mobile variant 2
 */
export function isFinalVerificationMobileVariant2(experimentValue: number | undefined): boolean {
  return experimentValue === 2;
}

/**
 * Determines if negative space tool should be shown based on the ab_design_lab_negative_space_tool experiment
 * Variant 1 (10%) shows the tool, variants 0,2-9 (90%) is control
 * @param experimentValue - The experiment variant value (0-9, where only 1 shows the feature)
 * @returns true if negative space tool should be shown, false if it should be hidden
 */
export function shouldShowNegativeSpaceTool(experimentValue: number | undefined): boolean {
  return experimentValue === 1;
}
